import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { MODAL_HOST_ID } from '@components/common/ModalHost';
import { cn } from '@/utils/cn';

interface StandardModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  showCloseButton?: boolean;
  /** Width class, defaults to max-w-2xl */
  maxWidth?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  fullScreen?: boolean;
  panelClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
}

export const StandardModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  headerActions,
  showCloseButton = true,
  maxWidth,
  size = '2xl',
  fullScreen = false,
  panelClassName,
  bodyClassName = 'flex-1 overflow-y-auto p-6 space-y-6 text-gray-700',
  footerClassName = 'px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3 flex-shrink-0',
}: StandardModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Focus trap could go here
      dialogRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClass = maxWidth || `max-w-${size}`;
  const portalTarget = document.getElementById(MODAL_HOST_ID) ?? document.body;
  const overlayClassName = fullScreen
    ? 'fixed inset-0 bg-night/50 flex items-center justify-center z-[100]'
    : 'fixed inset-0 bg-night/50 flex items-center justify-center z-[100] p-4';
  const dialogClassName = fullScreen
    ? 'h-screen max-h-screen max-w-none rounded-none'
    : cn(sizeClass, 'max-h-[90vh] rounded-lg');

  return createPortal(
    <div className={overlayClassName} aria-modal="true" role="dialog">
      {/* Backdrop click handler */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0" onClick={onClose} />

      <div
        ref={dialogRef}
        className={cn(
          'bg-white shadow-xl w-full flex flex-col z-10 outline-none overflow-hidden animate-in fade-in zoom-in-95 duration-200',
          dialogClassName,
          panelClassName
        )}
        onKeyDownCapture={event => event.stopPropagation()}
        onKeyUpCapture={event => event.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="text-xl font-bold text-gray-900">{title}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerActions}
            {showCloseButton ? (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                aria-label="Close"
                type="button"
              >
                <X size={24} />
              </button>
            ) : null}
          </div>
        </div>

        {/* Body */}
        <div className={bodyClassName}>{children}</div>

        {/* Footer */}
        {footer && <div className={footerClassName}>{footer}</div>}
      </div>
    </div>,
    portalTarget
  );
};
